import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Download,
  Loader2,
  ArrowLeft,
  FileText,
  Clock,
  File,
} from 'lucide-react';
import { Alert } from 'src/components/common/Alert';
import { StatusPill } from 'src/components/ui/status-pill';
import { PageHeader } from 'src/components/common/PageHeader';
import { useToast } from 'src/hooks/use-toast';
import { useCreditNoteInfo } from 'src/services/api/queries/billingQueries';
import { useApproveCreditNote } from 'src/services/api/mutations/billingMutations';
import { Textarea } from 'src/components/ui/textarea';
import { PDFDownloadLink } from '@react-pdf/renderer';
import CreditNotesPDF from 'src/components/CreditNotesPDF/CreditNotesPDF';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';
import { normalizeCtcToLpa } from 'src/lib/utils';
import { BILLING_STATUS_VALUES } from '../../../utils/statusConstants';

// Value in lakhs with the "LPA" suffix on the cell, one decimal, no ₹ — the
// column header is just "CTC" now, so the cell carries the unit instead.
// CTC's unit is ambiguous across records (plain rupees vs. already LPA), so
// it goes through the shared normalizeCtcToLpa first — see its usage below.
const formatLpa = (lpaValue) =>
  lpaValue == null ? '-' : `${lpaValue.toFixed(1)} LPA`;

// Same "first invoice + overflow badge" treatment as
// CreateCreditNoteModal.jsx's invoice-numbers strip — a credit note can
// span several invoices (invoice_ids sent as an array on create), so this
// keeps a multi-invoice cell compact instead of wrapping a long list.
const InvoiceNumbersCell = ({ invoiceNumbers }) => {
  if (!invoiceNumbers || invoiceNumbers.length === 0) return '-';
  const [first, ...rest] = invoiceNumbers;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-[11.5px]">{first}</span>
      {rest.length > 0 && (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center justify-center text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-border text-foreground-strong cursor-default">
                +{rest.length}
              </span>
            </TooltipTrigger>
            <TooltipContent>{rest.join(', ')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
};

// Unchanged — CreditNotesPDF.jsx reads creditNoteNumber/creditNoteDate/
// poNumber/client/ahedge/creditNoteDetails/candidateDetails straight off this
// shape, so the PDF export stays working while the page below is rebuilt to
// match InvoiceInfo.jsx/ProformaInvoiceInfo.jsx's layout.
const transformApiResponse = (apiData) => {
  if (!apiData) {
    throw new Error('No API data received');
  }

  const {
    header = {},
    candidate_details: candidateDetails = {},
    credit_note_details: creditNoteDetails = {},
    meta = {},
  } = apiData;
  if (!header || !creditNoteDetails || !meta) {
    throw new Error('Invalid API response structure');
  }

  return {
    candidateDetails,
    id: meta.id || 'N/A',
    creditNoteNumber: header.credit_note_number || 'N/A',
    creditNoteDate: header.issued_date || 'N/A',
    poNumber: header.po_number || 'N/A',
    poId: meta.po_id || 'N/A',
    client: {
      name: header.customer?.name || 'N/A',
      id: header.customer?.id || 'N/A',
      gstin: header.customer?.gstin || 'N/A',
      address: {
        line1: header.customer?.address_line_1 || '',
        street: header.customer?.street || '',
        zipCode: header.customer?.zip_code || '',
      },
    },
    ahedge: {
      name: header.ahedge?.name || 'N/A',
      address: header.ahedge?.address || 'N/A',
      cityStatePin: header.ahedge?.city_state_pin || 'N/A',
      gstin: header.ahedge?.gstin || 'N/A',
    },
    creditNoteDetails: {
      // Real response (confirmed): reason/reason_code/details/amount/
      // gst_amount/total_amount/status — not credit_note_type/
      // amount_before_tax/tax_amount/tax_percentage as originally guessed.
      // No percentage field is sent, so it's derived from amount/gst_amount.
      creditNoteType: creditNoteDetails.reason || 'N/A',
      reasonCode: creditNoteDetails.reason_code || '',
      details: creditNoteDetails.details || '',
      subTotal: creditNoteDetails.amount || 0,
      taxAmount: creditNoteDetails.gst_amount || 0,
      taxPercentage: creditNoteDetails.amount
        ? Math.round(
            (creditNoteDetails.gst_amount / creditNoteDetails.amount) * 100
          )
        : 0,
      totalAmount: creditNoteDetails.total_amount || 0,
      currency: creditNoteDetails.currency || 'INR',
      totalPaid: creditNoteDetails.total_paid || 0,
      balanceDue: creditNoteDetails.balance_due || 0,
    },
    meta: {
      id: meta.id || 'N/A',
      status: creditNoteDetails.status || 'N/A',
      customerCategory: meta.customer_category || 'N/A',
      milestoneType: meta.milestone_type || 'N/A',
      createdAt: meta.created_at || 'N/A',
      updatedAt: meta.updated_at || 'N/A',
    },
  };
};

// Same InfoCard as InvoiceInfo.jsx/ProformaInvoiceInfo.jsx — kept identical
// so this page reads as the same design, not a near-miss.
const InfoCard = ({ title, children, className = '', rounded = false }) => (
  <div
    className={`bg-card p-4 ${className} ${rounded ? 'rounded-xl border border-border' : ''}`}
  >
    {title && (
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2">
        {title}
      </h3>
    )}
    {children}
  </div>
);

// Same StatusPill-based pattern as InvoiceInfo/ProformaInvoiceInfo's own
// StatusTag, with credit notes' own status vocabulary (DRAFT/ISSUED/
// APPROVED — see ListCreditNotes.jsx's draftCount/issuedCount split).
const StatusTag = ({ status }) => {
  const label = BILLING_STATUS_VALUES[status] || status;
  let textColor = 'slate-600';
  let borderColor = 'slate-200';
  let bgColor = 'slate-50';
  let icon = null;

  switch (status) {
    case 'DRAFT':
      textColor = 'blue-700';
      borderColor = 'blue-200';
      bgColor = 'blue-50';
      icon = <File className="w-3.5 h-3.5" />;
      break;
    case 'ISSUED':
      textColor = 'amber-700';
      borderColor = 'amber-200';
      bgColor = 'amber-50';
      icon = <Clock className="w-3.5 h-3.5" />;
      break;
    case 'APPROVED':
      textColor = 'emerald-600';
      borderColor = 'emerald-200';
      bgColor = 'emerald-50';
      icon = <CheckCircle className="w-3.5 h-3.5" />;
      break;
    default:
      break;
  }

  return (
    <StatusPill
      name={label}
      textColor={textColor}
      borderColor={borderColor}
      bgColor={bgColor}
      icon={icon}
    />
  );
};

const CreditNotesInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: response, isLoading, isError, error } = useCreditNoteInfo(id);
  const [showVerifyAlert, setShowVerifyAlert] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const approveCreditNoteMutation = useApproveCreditNote();
  const isApproving = approveCreditNoteMutation.isPending;

  let creditNoteData = null;
  let transformationError = null;
  if (response?.success) {
    try {
      creditNoteData = transformApiResponse(response.data);
    } catch (err) {
      transformationError = err.message;
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4">Loading credit note details...</p>
      </div>
    );
  }

  if (isError || transformationError || (response && !response.success)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-500 p-4 border border-red-300 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">
            Error Loading Credit Note
          </h2>
          <p>
            {error?.message ||
              transformationError ||
              response?.message ||
              'Failed to load credit note information'}
          </p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!creditNoteData) {
    return <div>No credit note data found.</div>;
  }

  const handleVerifyCreditNote = async () => {
    try {
      const result = await approveCreditNoteMutation.mutateAsync({
        id,
        data: { approval_notes: approvalNotes.trim() },
      });
      if (result?.success) {
        toast({
          title: 'Success',
          description: 'Credit note has been approved successfully',
        });
        setShowVerifyAlert(false);
        setApprovalNotes('');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to approve credit note',
        variant: 'destructive',
      });
    }
  };

  const handleCloseAlert = () => {
    setShowVerifyAlert(false);
    setApprovalNotes('');
  };

  const currencySymbol =
    creditNoteData.creditNoteDetails.currency === 'INR' ? '₹' : '$';
  const invoiceItems = response.data.invoice_details || [];
  // Falls back to one row off candidateDetails when there's no per-invoice
  // breakdown yet, so the items table is never left empty for a credit note
  // that's otherwise fully loaded.
  const itemRows =
    invoiceItems.length > 0
      ? invoiceItems
      : [
          {
            invoice_item_id: 'candidate-summary',
            item_amount: creditNoteData.creditNoteDetails.subTotal,
          },
        ];
  // Every invoice this credit note was raised against (invoice_ids was sent
  // as an array on create — see CreateCreditNoteModal.jsx) — not yet
  // confirmed against a real multi-invoice sample, so falls back to
  // collecting each row's own invoice_number when this top-level field
  // isn't present.
  const creditNoteInvoiceNumbers =
    response.data.invoice_numbers ||
    itemRows.map((item) => item.invoice_number).filter(Boolean);
  // Candidate is optional on a credit note (see CreateCreditNoteModal.jsx) —
  // candidate_details comes back as {} rather than omitted when there isn't
  // one, so check for an actual value rather than just object truthiness.
  // candidate_joining_details_id isn't confirmed to be nested under
  // candidate_details on THIS endpoint's response (only on the
  // joined-candidates list and the create payload) — candidate_name is,
  // since the table below already renders it directly, so key off that
  // instead. Without a candidate, the Summary line-item table and the
  // Credit Note Items table below have nothing meaningful to show — just
  // the amount/GST/grand total breakdown stays.
  const hasCandidate = !!creditNoteData.candidateDetails?.candidate_name;

  return (
    <div className="text-left space-y-4 bg-muted">
      <PageHeader
        title={`Credit Note # ${creditNoteData.creditNoteNumber}`}
        actions={
          <>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate('/credit-notes')}
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              Back to Credit Notes
            </Button>
            <PDFDownloadLink
              document={
                <CreditNotesPDF
                  data={{
                    ...creditNoteData,
                    invoiceNumbers: creditNoteInvoiceNumbers,
                  }}
                />
              }
              fileName={`CreditNote_${creditNoteData.creditNoteNumber}.pdf`}
              className="no-underline"
            >
              {({ loading }) => (
                <Button
                  disabled={loading}
                  variant="outline"
                  size="headerSm"
                  className="gap-1.5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      Download PDF
                      <Download className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </PDFDownloadLink>
          </>
        }
      />

      <div className="p-4 px-40 flex gap-4 flex-col">
        <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-md text-primary flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Credit Note
            </h2>
            {creditNoteData.meta.status === 'DRAFT' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-emerald-500 text-white hover:bg-emerald-600"
                onClick={() => setShowVerifyAlert(true)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-border pb-6">
            <InfoCard title="From" className="pl-0">
              <h4 className="text-sm font-semibold text-base">
                {creditNoteData.ahedge.name}
              </h4>
              <p className="text-xs text-gray-600 mt-1">
                {creditNoteData.ahedge.address}
              </p>
              <p className="text-xs text-gray-600">
                {creditNoteData.ahedge.cityStatePin}
              </p>
              <p className="text-xs mt-2 flex gap-2 items-center">
                <span className="text-muted-foreground font-normal text-[11px]">
                  GSTIN:
                </span>{' '}
                <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                  {creditNoteData.ahedge.gstin}
                </span>
              </p>
            </InfoCard>

            <InfoCard title="To">
              <h4 className="text-sm font-semibold text-base">
                {creditNoteData.client.name}
              </h4>
              {creditNoteData.client.address && (
                <>
                  {creditNoteData.client.address.line1 && (
                    <p className="text-xs text-gray-600 mt-1">
                      {creditNoteData.client.address.line1}
                    </p>
                  )}
                  {(creditNoteData.client.address.street ||
                    creditNoteData.client.address.zipCode) && (
                    <p className="text-xs text-gray-600">
                      {[
                        creditNoteData.client.address.street,
                        creditNoteData.client.address.zipCode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </>
              )}
              {creditNoteData.client.gstin &&
                creditNoteData.client.gstin !== 'N/A' && (
                  <p className="text-xs mt-2 flex gap-2 items-center">
                    <span className="text-muted-foreground font-normal text-[11px]">
                      GSTIN:
                    </span>{' '}
                    <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                      {creditNoteData.client.gstin}
                    </span>
                  </p>
                )}
            </InfoCard>

            <InfoCard
              rounded
              className="!bg-card border border-border grid grid-cols-2 content-start gap-4"
            >
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-700">Issued Date</span>
                <span className="text-xs font-bold">
                  {creditNoteData.creditNoteDate}
                </span>
              </div>
              {creditNoteData.poNumber && creditNoteData.poNumber !== 'N/A' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-700">PO Number</span>
                  <span className="text-xs font-bold">
                    {creditNoteData.poNumber}
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-700">Status</span>
                <span className="text-xs font-bold w-fit">
                  <StatusTag status={creditNoteData.meta.status} />
                </span>
              </div>
            </InfoCard>
          </div>

          <InfoCard title="SUMMARY" className="mb-6 px-0">
            {hasCandidate && (
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-border">
                      <tr className="bg-card text-muted-foreground">
                        <th className="p-3 py-2 text-left font-normal">
                          DESCRIPTION
                        </th>
                        <th className="p-3 py-2 text-right font-normal">NOS</th>
                        <th className="p-3 py-2 text-right font-normal">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr key="item-summary" className="border-b border-border">
                        <td className="p-3 font-medium">
                          {creditNoteData.candidateDetails?.candidate_name ||
                            creditNoteData.creditNoteDetails.creditNoteType}
                        </td>
                        <td className="p-3 text-right">1</td>
                        <td className="p-3 text-right">
                          {currencySymbol}
                          {creditNoteData.creditNoteDetails.subTotal?.toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!hasCandidate && (
              <div className="px-4 pt-4 text-xs space-y-1.5">
                <div>
                  <span className="text-muted-foreground">Reason: </span>
                  <span className="font-medium text-foreground-strong">
                    {creditNoteData.creditNoteDetails.creditNoteType}
                  </span>
                </div>
                {creditNoteData.creditNoteDetails.details && (
                  <div>
                    <span className="text-muted-foreground">Details: </span>
                    <span className="font-medium text-foreground-strong">
                      {creditNoteData.creditNoteDetails.details}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end p-4 pt-3">
              <div className="w-full max-w-xs text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="text-foreground-strong font-medium">
                    {currencySymbol}
                    {creditNoteData.creditNoteDetails.subTotal?.toLocaleString(
                      'en-IN',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    GST ({creditNoteData.creditNoteDetails.taxPercentage}%)
                  </span>
                  <span className="text-foreground-strong font-medium">
                    {currencySymbol}
                    {creditNoteData.creditNoteDetails.taxAmount?.toLocaleString(
                      'en-IN',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
                <div className="border-t border-border my-2" />
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground-strong">
                    Grand Total
                  </span>
                  <span
                    className="text-[15px] font-bold text-primary"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {currencySymbol}
                    {creditNoteData.creditNoteDetails.totalAmount?.toLocaleString(
                      'en-IN',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>

          <div className="mt-8 space-y-6">
            <div className="bg-muted border border-border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">
                This is a system-generated credit note. No signature required.
              </p>
            </div>

            <div className="flex flex-col gap-4 justify-between items-start pt-4">
              <div className="flex-1">
                <h4 className="font-bold text-xs mb-2">Terms</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>
                    • This credit note offsets the referenced invoice(s) against
                    future billing.
                  </li>
                  <li>• For queries, contact billing@ahedge.in</li>
                </ul>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Authorised by
                  <span className="text-xs font-medium text-foreground-strong mb-2 ml-1">
                    Ahedge Finance
                  </span>
                </div>
                <div className="text-xs text-muted-foreground italic">
                  Digitally approved — no signature required
                </div>
              </div>
            </div>
          </div>
        </div>

        {hasCandidate && (
          <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
            <InfoCard title="CREDIT NOTE ITEMS" className="mb-6 px-0">
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-border">
                      <tr className="bg-card text-muted-foreground">
                        <th className="p-3 py-2 text-left font-normal">
                          CANDIDATE
                        </th>
                        <th className="p-3 py-2 text-left font-normal">
                          REQUIREMENT
                        </th>
                        <th className="p-3 py-2 text-left font-normal">
                          INVOICE NUMBER
                        </th>
                        <th className="p-3 py-2 text-right font-normal">CTC</th>
                        <th className="p-3 py-2 text-right font-normal">
                          AMOUNT
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemRows.map((item, index) => (
                        <tr
                          key={item.invoice_item_id || index}
                          className={
                            index < itemRows.length - 1
                              ? 'border-b border-border'
                              : ''
                          }
                        >
                          <td className="p-3 font-medium">
                            {creditNoteData.candidateDetails?.candidate_name ||
                              '-'}
                          </td>
                          <td className="p-3">
                            {creditNoteData.candidateDetails
                              ?.requirement_title || '-'}
                          </td>
                          <td className="p-3">
                            <InvoiceNumbersCell
                              invoiceNumbers={
                                // Each row carries its own invoice_number
                                // once there's a real per-invoice
                                // breakdown; the single fallback row (no
                                // breakdown yet) shows every invoice this
                                // credit note spans instead.
                                itemRows.length > 1
                                  ? [item.invoice_number].filter(Boolean)
                                  : creditNoteInvoiceNumbers
                              }
                            />
                          </td>
                          <td className="p-3 text-right">
                            {formatLpa(
                              normalizeCtcToLpa(
                                creditNoteData.candidateDetails?.ctc_amount
                              )
                            )}
                          </td>
                          <td className="p-3 text-right font-medium">
                            {item.item_amount == null
                              ? '-'
                              : `${currencySymbol}${item.item_amount.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </InfoCard>
          </div>
        )}
      </div>

      <Alert
        isOpen={showVerifyAlert}
        onClose={handleCloseAlert}
        title="Approve Credit Note"
        description={`Are you sure you want to approve credit note ${creditNoteData.creditNoteNumber}?`}
        cancelText="Cancel"
        continueText={isApproving ? 'Processing...' : 'Approve'}
        onCancel={handleCloseAlert}
        onContinue={handleVerifyCreditNote}
        customContent={
          <div className="mt-4">
            <div className="block text-sm font-medium text-gray-700 mb-2">
              Approval Notes (optional)
            </div>
            <Textarea
              id="approval-notes-textarea"
              name="approval-notes"
              placeholder="Enter approval notes..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              maxLength={50}
              className="w-full"
              rows={3}
              aria-label="Approval Notes (optional)"
              aria-describedby="approval-notes-counter"
            />
            <div
              id="approval-notes-counter"
              className="text-xs text-gray-500 mt-1"
            >
              {approvalNotes.length}/50 characters
            </div>
          </div>
        }
      />
    </div>
  );
};

export default CreditNotesInfo;
